/**
 * Upsell Modal Manager
 * Encapsulates all logic for the subscription upsell offer presented on submit.
 */
(function() {
  class UpsellModalManager {
    constructor(options) {
      this.productForm = options.productForm;
      this.form = options.formElement;
      this.modal = options.modalElement;
      this.moneyFormat = options.moneyFormat;
      this.defaultMultiplier = options.defaultMultiplier || 3;
      this.onSubmit = options.onSubmit;
      this.logger = console;
      this.productId = options.productId || this.form?.dataset?.productId || null;
      this.productTitle = options.productTitle || window?.globalVariables?.product?.title || null;
      this.currency = options.currency || window?.Shopify?.currency?.active || 'USD';
      this.modalVariant = options.modalVariant || 'default';
      this.currentEventId = null;

      if (!this.modal || !this.form) {
        this.enabled = false;
        return;
      }

      this.enabled = true;

      this.variantData = {};
      if (options.variantMetafieldJSON) {
        try {
          this.variantData = JSON.parse(options.variantMetafieldJSON) || {};
        } catch (err) {
          this.logger.error('[Upsell] Failed to parse variant metafields JSON', err);
          this.variantData = {};
        }
      }

      this.headingEl = this.modal.querySelector('[data-upsell-heading]');
      this.subheadingEl = this.modal.querySelector('[data-upsell-subheading]');
      this.detailsEl = this.modal.querySelector('[data-upsell-details]');
      this.pricingEl = this.modal.querySelector('[data-upsell-pricing]');
      this.imageWrapper = this.modal.querySelector('[data-upsell-image-wrapper]');
      this.imageEl = this.modal.querySelector('[data-upsell-image]');
      this.acceptBtn = this.modal.querySelector('[data-upsell-accept]');
      this.declineBtn = this.modal.querySelector('[data-upsell-decline]');
      this.closeBtn = this.modal.querySelector('[data-upsell-modal-close]');

      this.pendingContext = null;

      this.handleAccept = this.handleAccept.bind(this);
      this.handleDecline = this.handleDecline.bind(this);

      if (this.acceptBtn) this.acceptBtn.addEventListener('click', this.handleAccept);
      if (this.declineBtn) this.declineBtn.addEventListener('click', this.handleDecline);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.handleDecline);
    }

    generateEventId() {
      return `upsell-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    }

    /**
     * Deep-clone a payload for mutation. Avoids "Converting circular structure to JSON"
     * if the object was contaminated with window/document (e.g. by an extension).
     */
    safePayloadClone(obj) {
      if (!obj || typeof obj !== 'object') return obj;
      try {
        return JSON.parse(JSON.stringify(obj));
      } catch (e) {
        const safe = {};
        const keys = ['id', 'quantity', 'selling_plan', 'selling_plan_group', 'properties'];
        keys.forEach(k => {
          if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] !== undefined) {
            safe[k] = obj[k] && typeof obj[k] === 'object' && !Array.isArray(obj[k])
              ? { ...obj[k] }
              : obj[k];
          }
        });
        return safe;
      }
    }

    pushTrackingEvent(eventName, context, extra = {}) {
      if (!eventName || !context) return;
      if (!this.currentEventId) {
        this.currentEventId = this.generateEventId();
      }

      const data = {
        event: eventName,
        event_id: this.currentEventId,
        original_product_id: this.productId ? String(this.productId) : undefined,
        original_product_name: this.productTitle || undefined,
        original_variant_id: context.payload?.id ? String(context.payload.id) : undefined,
        original_selling_plan_id: context.basePlan?.id ? String(context.basePlan.id) : undefined,
        original_selling_plan_name: context.basePlan?.name || undefined,
        original_price: context.originalPriceValue !== undefined && context.originalPriceValue !== null ? context.originalPriceValue : undefined,
        original_price_formatted: context.formattedBaseTotal || undefined,
        offer_product_id: context.upsellData?.product_id ? String(context.upsellData.product_id) : undefined,
        offer_product_name: context.upsellData?.product_title || undefined,
        offer_variant_id: context.upsellData?.variant_id ? String(context.upsellData.variant_id) : undefined,
        offer_selling_plan_id: context.upsellPlan?.id ? String(context.upsellPlan.id) : undefined,
        offer_selling_plan_name: context.upsellPlan?.name || undefined,
        offer_price: context.upsellPlan?.price ? Number(context.upsellPlan.price / 100) : undefined,
        offer_price_formatted: context.formattedUpsellPrice || undefined,
        offer_compare_price_formatted: context.formattedCompare || undefined,
        currency: this.currency,
        modal_variant: this.modalVariant
      };

      if (context.formattedSavings) {
        data.offer_total_savings = context.formattedSavings;
      }
      if (context.savingsValue !== undefined && context.savingsValue !== null) {
        data.offer_total_savings_value = context.savingsValue;
      }
      if (context.formattedPerUnitSavings) {
        data.offer_per_unit_savings = context.formattedPerUnitSavings;
      }
      if (context.perUnitSavingsValue !== undefined && context.perUnitSavingsValue !== null) {
        data.offer_per_unit_savings_value = context.perUnitSavingsValue;
      }

      Object.assign(data, extra);

      Object.keys(data).forEach((key) => {
        if (data[key] === undefined || data[key] === null || data[key] === '') {
          delete data[key];
        }
      });

      // Ensure dataLayer only receives JSON-serializable data (no Window/DOM refs).
      // TikTok pixel and other scripts may JSON.stringify dataLayer; circular refs cause "Converting circular structure to JSON".
      let payload;
      try {
        payload = JSON.parse(JSON.stringify(data));
      } catch (e) {
        payload = {};
        Object.keys(data).forEach((key) => {
          const v = data[key];
          const t = typeof v;
          if (v !== undefined && v !== null && (t === 'string' || t === 'number' || t === 'boolean')) {
            payload[key] = v;
          }
        });
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(payload);
    }

    handleSubmit(itemPayload) {
      if (!this.enabled) return false;
      const context = this.buildContext(itemPayload);
      if (!context) return false;

      this.currentEventId = this.generateEventId();
      context.eventId = this.currentEventId;
      this.render(context);
      this.pushTrackingEvent('upsell_qty_modal_open', this.pendingContext || context);
      this.show();

      return true;
    }

    buildContext(itemPayload) {
      if (!itemPayload || !itemPayload.id) return null;

      const selectedSellingPlan = itemPayload?.selling_plan;
      if (!selectedSellingPlan && selectedSellingPlan !== 0) {
        return null;
      }

      const baseVariantMeta = this.variantData?.[itemPayload.id];
      if (!baseVariantMeta || !baseVariantMeta.upsell) {
        return null;
      }

      const upsellData = baseVariantMeta.upsell;
      if (!upsellData || !upsellData.variant_id) {
        return null;
      }

      const basePlans = Array.isArray(baseVariantMeta.selling_plans) ? baseVariantMeta.selling_plans : [];
      const basePlan = basePlans.find(plan => plan.id == selectedSellingPlan);
      if (!basePlan) {
        return null;
      }

      const multiplierValue = this.parseMultiplier(upsellData.multiplier);
      const baseCadence = this.getPlanCadence(basePlan, upsellData);
      let baseIntervalCount = baseCadence.count;
      let baseIntervalUnit = baseCadence.unit;
      if (!baseIntervalCount) {
        baseIntervalCount = 1;
      }

      const upsellPlans = Array.isArray(upsellData.selling_plans) ? upsellData.selling_plans : [];
      const upsellPlanCadences = upsellPlans.map(plan => ({
        plan,
        cadence: this.getPlanCadence(plan, upsellData)
      }));

      if (!baseIntervalUnit && upsellPlanCadences.length) {
        const fallbackCadence = upsellPlanCadences[0].cadence;
        baseIntervalUnit = fallbackCadence.unit;
      }

      if (!baseIntervalCount || !baseIntervalUnit) {
        return null;
      }

      const targetIntervalCount = baseIntervalCount * multiplierValue;

      const exactMatch = upsellPlanCadences.find(({ cadence }) => (
        cadence.unit === baseIntervalUnit && cadence.count === targetIntervalCount
      ));
      let selectedPlan = exactMatch;

      if (!selectedPlan) {
        const sameUnitPlans = upsellPlanCadences
          .filter(({ cadence }) => cadence.unit === baseIntervalUnit && cadence.count);

        if (sameUnitPlans.length) {
          selectedPlan = sameUnitPlans
            .sort((a, b) => a.cadence.count - b.cadence.count)
            .find(({ cadence }) => cadence.count >= targetIntervalCount) || sameUnitPlans[sameUnitPlans.length - 1];
        }
      }

      if (!selectedPlan && upsellPlanCadences.length) {
        selectedPlan = upsellPlanCadences[0];
      }

      if (!selectedPlan) {
        return null;
      }

      const upsellPlan = selectedPlan.plan;
      const upsellCadence = selectedPlan.cadence;
      const upsellIntervalCount = upsellCadence.count || targetIntervalCount;
      const upsellIntervalUnit = upsellCadence.unit || baseIntervalUnit;
      const planMatchesTarget = upsellCadence.unit === baseIntervalUnit && upsellCadence.count === targetIntervalCount;

      return {
        payload: this.safePayloadClone(itemPayload),
        baseVariantId: itemPayload.id,
        basePlan,
        upsellPlan,
        upsellData,
        multiplier: multiplierValue,
        baseIntervalCount,
        baseIntervalUnit,
        targetIntervalCount,
        upsellIntervalCount,
        upsellIntervalUnit,
        planMatchesTarget
      };
    }

    render(context) {
      const basePrice = context.basePlan?.price || 0;
      const basePlanCompareAtPrice = context.basePlan?.compare_at_price || 0;
      
      // Try to get variant's compare_at_price as fallback if plan's compare_at_price is not available or seems incorrect
      // The variant's compare_at_price is the original product price before any subscription discounts
      const baseVariantMeta = this.variantData?.[context.baseVariantId];
      const variantCompareAtPrice = baseVariantMeta?.compare_at_price || 0;
      
      // Use variant's compare_at_price if plan's compare_at_price is 0 or missing
      // Prefer variant compare_at_price as it represents the true original product price
      const baseCompareAtPrice = variantCompareAtPrice || basePlanCompareAtPrice;
      
      const upsellPrice = context.upsellPlan?.price || context.upsellData?.price || 0;
      const upsellCompare = context.upsellPlan?.compare_at_price || context.upsellData?.compare_at_price || null;
      const multiplier = context.multiplier || 1;

      // Calculate baseTotal accounting for Recharge subscription pricing:
      // - First order: Uses sale price (basePrice)
      // - Subsequent orders: 10% off original price (compare_at_price * 0.9)
      const firstOrderPrice = basePrice;
      const originalPrice = baseCompareAtPrice || basePrice;
      const subsequentOrderPrice = originalPrice * 0.9; // 10% off for future orders
      
      let baseTotal = 0;
      if (multiplier > 0) {
        if (multiplier === 1) {
          baseTotal = firstOrderPrice;
        } else {
          // First order at sale price + remaining orders at 10% off original
          const remainingOrders = multiplier - 1;
          baseTotal = firstOrderPrice + (remainingOrders * subsequentOrderPrice);
        }
      }

      let savingsAmount = 0;

      if (baseTotal > 0 && upsellPrice > 0 && baseTotal > upsellPrice) {
        savingsAmount = baseTotal - upsellPrice;
      } else if (baseTotal > 0 && upsellCompare && baseTotal > upsellCompare) {
        savingsAmount = baseTotal - upsellCompare;
      }

      const savingsPercent = baseTotal > 0 && savingsAmount > 0 ? Math.round((savingsAmount / baseTotal) * 100) : 0;
      const formattedBaseTotal = Utility.formatMoney(baseTotal, this.moneyFormat);
      const formattedUpsellPrice = Utility.formatMoney(upsellPrice, this.moneyFormat);
      const formattedUpsellCompare = (upsellCompare && upsellCompare !== upsellPrice) ? Utility.formatMoney(upsellCompare, this.moneyFormat) : null;
      const formattedSavings = savingsAmount > 0 ? Utility.formatMoney(savingsAmount, this.moneyFormat) : null;
      const perUnitSavingsAmount = (savingsAmount > 0 && context.multiplier) ? savingsAmount / context.multiplier : 0;
      const formattedPerUnitSavings = perUnitSavingsAmount > 0 ? Utility.formatMoney(perUnitSavingsAmount, this.moneyFormat) : null;
      const originalPriceValue = context.basePlan?.price ? Number(context.basePlan.price / 100) : null;
      const savingsValue = savingsAmount > 0 ? Number(savingsAmount / 100) : null;
      const perUnitSavingsValue = perUnitSavingsAmount > 0 ? Number(perUnitSavingsAmount / 100) : null;

      context.formattedSavings = formattedSavings;
      context.formattedPerUnitSavings = formattedPerUnitSavings;
      context.formattedBaseTotal = formattedBaseTotal;
      context.formattedUpsellPrice = formattedUpsellPrice;
      context.formattedCompare = formattedUpsellCompare;
      context.originalPriceValue = originalPriceValue;
      context.savingsValue = savingsValue;
      context.perUnitSavingsValue = perUnitSavingsValue;

      const defaults = this.getUpsellDefaults(context);

      let headingText = (context.upsellData.headline || '').trim() || defaults.heading;
      if (!(context.upsellData.headline || '').trim()) {
        headingText = headingText.toUpperCase();
      }
      const bodyText = (context.upsellData.body || '').trim() || defaults.body();
      const acceptLabel = (context.upsellData.accept_label || '').trim() || defaults.acceptLabel();
      const declineLabel = (context.upsellData.decline_label || '').trim() || defaults.declineLabel;

      if (this.headingEl) this.headingEl.textContent = headingText;
      if (this.subheadingEl) {
        if (bodyText) {
          this.subheadingEl.innerHTML = bodyText;
          this.subheadingEl.classList.remove('d-none');
        } else {
          this.subheadingEl.innerHTML = '';
          this.subheadingEl.classList.add('d-none');
        }
      }

      if (this.acceptBtn) this.acceptBtn.textContent = acceptLabel;
      if (this.declineBtn) this.declineBtn.textContent = declineLabel;

      if (this.detailsEl) {
        const savingsHighlight = defaults.savingsHighlight();
        const planetHighlight = defaults.planetHighlight();

        const savingsHighlightHtml = `
          <div class="subscription-upsell-modal__highlight subscription-upsell-modal__highlight--savings">
            <span class="subscription-upsell-modal__highlight-icon" aria-hidden="true">${defaults.savingsIcon}</span>
            <span class="subscription-upsell-modal__highlight-text">${savingsHighlight}</span>
          </div>
        `;

        const planetHighlightHtml = `
          <div class="subscription-upsell-modal__highlight subscription-upsell-modal__highlight--planet">
            <span class="subscription-upsell-modal__highlight-icon" aria-hidden="true">${defaults.planetIcon}</span>
            <span class="subscription-upsell-modal__highlight-text">${planetHighlight}</span>
          </div>
        `;

        this.detailsEl.innerHTML = `
          <div class="subscription-upsell-modal__highlights">
            ${savingsHighlightHtml}
            ${planetHighlightHtml}
          </div>
        `;
      }

      if (this.pricingEl) {
        this.pricingEl.innerHTML = '';
      }

      if (this.imageWrapper && this.imageEl) {
        const primaryImage = context.upsellData.image && context.upsellData.image.src ? context.upsellData.image : null;
        const fallbackImage = context.upsellData.product_image && context.upsellData.product_image.src ? context.upsellData.product_image : null;
        const imageToUse = primaryImage || fallbackImage;

        if (imageToUse) {
          this.imageEl.src = imageToUse.src;
          this.imageEl.alt = imageToUse.alt || headingText;
          this.imageEl.classList.remove('d-none');
        } else {
          this.imageEl.src = '';
          this.imageEl.classList.add('d-none');
        }
      }

      this.pendingContext = {
        ...context,
        formattedBaseTotal,
        formattedUpsellPrice,
        formattedCompare: formattedUpsellCompare,
        formattedSavings: context.formattedSavings,
        formattedPerUnitSavings
      };
    }

    show() {
      if (typeof this.modal.show === 'function') {
        const opener = this.productForm.submitButton || this.form.querySelector('[type="submit"]');
        this.modal.show(opener || this.form);
      }
    }

    hide() {
      if (typeof this.modal.hide === 'function') {
        this.modal.hide();
      }
    }

    handleAccept(event) {
      if (event) event.preventDefault();
      if (!this.pendingContext) {
        this.hide();
        return;
      }
      const context = this.pendingContext;
      const upgradedPayload = this.safePayloadClone(context.payload);
      upgradedPayload.id = context.upsellData.variant_id;
      upgradedPayload.selling_plan = context.upsellPlan.id;
      upgradedPayload.quantity = upgradedPayload.quantity || 1;
      upgradedPayload.properties = upgradedPayload.properties || {};
      upgradedPayload.properties['_upsell_parent_variant'] = context.baseVariantId?.toString?.() || context.baseVariantId;
      upgradedPayload.properties['_upsell_multiplier'] = context.multiplier?.toString?.() || `${context.multiplier}`;
      upgradedPayload.properties['_upsell_offer'] = context.upsellData.variant_id;

      this.pushTrackingEvent('upsell_qty_modal_accept', context);
      this.pendingContext = null;
      this.hide();
      this.currentEventId = null;

      if (typeof this.onSubmit === 'function') {
        this.onSubmit(upgradedPayload);
      }
    }

    handleDecline(event) {
      if (event) event.preventDefault();
      const originalPayload = this.pendingContext?.payload;
      if (this.pendingContext) {
        this.pushTrackingEvent('upsell_qty_modal_decline', this.pendingContext);
      }
      this.pendingContext = null;
      this.hide();
      this.currentEventId = null;
      if (originalPayload && typeof this.onSubmit === 'function') {
        this.onSubmit(originalPayload);
      }
    }

    getUpsellDefaults(context) {
      const self = this;
      return {
        heading: 'STOCK UP & SAVE MORE',
        body: () => {
          const baseCadence = self.formatIntervalText(context.baseIntervalCount, context.baseIntervalUnit);
          const bundleCadence = self.formatIntervalText(context.upsellIntervalCount, context.upsellIntervalUnit);

          if (!baseCadence || !bundleCadence) {
            return 'Help your wallet and the planet. Upgrade from monthly tubs to one delivery every 3 months — same total supply, less cost.';
          }

          return `Help your wallet and the planet. Upgrade from <strong>1 tub ${baseCadence}</strong> to <strong>${context.multiplier} tubs ${bundleCadence}</strong> — same total supply, less cost.`;
        },
        // acceptLabel: () => context.formattedSavings ? `Upgrade & Save ${context.formattedSavings}` : 'Upgrade & Save',
        acceptLabel: () => context.formattedSavings ? `Upgrade & Save $10` : 'Upgrade & Save',
        declineLabel: 'No, thanks',
        savingsHighlight: () => {
          const priceOverwrite = 10;

          if (priceOverwrite){
            return `Save $${priceOverwrite} <span style="font-weight: 300;">with ${context.multiplier}-tubs per shipment</span>`;
          } else if (context.formattedSavings) {
            const multiplier = context.multiplier || 3;
            return `Save ${context.formattedSavings} <span style="font-weight: 300;">with ${multiplier}-tubs per shipment</span>`;
          }

          return 'Lock in instant savings with fewer deliveries.';
        },
        planetHighlight: () => {
          const shipmentsSaved = Math.max(context.multiplier - 1, 0);
          return `<strong>Go Green!</strong> ${shipmentsSaved > 0 ? `${shipmentsSaved} fewer shipments` : 'Fewer shipments'} means less packaging and lower carbon emissions.`;
        },
        savingsIcon: `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" role="presentation">
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
            <path d="M15 8.5C14.315 7.81501 13.1087 7.33855 12 7.30872" />
            <path d="M9 15C9.64448 15.8593 10.8428 16.3494 12 16.391" />
            <path d="M12 7.30872C10.6809 7.27322 9.5 7.86998 9.5 9.50001C9.5 12.5 15 11 15 14C15 15.711 13.5362 16.4462 12 16.391" />
            <path d="M12 7.30872V5.5" />
            <path d="M12 16.391V18.5" />
          </svg>
        `,
        planetIcon: `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" role="presentation">
            <path d="M9 18C9 18 9.9 14.2588 12 12" />
            <path d="M16.1857 7.24133L16.5601 11.1306C16.8031 13.6536 14.9114 15.9001 12.3883 16.143C9.91266 16.3814 7.67 14.5718 7.43162 12.0962C7.19324 9.62055 9.00691 7.4204 11.4826 7.18203L15.6293 6.78274C15.9096 6.75575 16.1587 6.96107 16.1857 7.24133Z" />
            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" />
          </svg>
        `
      };
    }

    formatIntervalText(intervalCount, intervalUnit) {
      const count = this.parseIntervalCount(intervalCount) || 1;
      let unitLabel = this.normalizeIntervalUnit(intervalUnit);

      if (!unitLabel) {
        return '';
      }

      if (count === 1) {
        return `every ${unitLabel}`;
      }

      return `every ${count} ${unitLabel}${count > 1 ? 's' : ''}`;
    }

    parseMultiplier(value) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
      return this.defaultMultiplier;
    }

    parseIntervalCount(value) {
      const parsed = parseInt(value, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
      return 0;
    }

    normalizeIntervalUnit(value) {
      if (!value || typeof value !== 'string') return '';
      const clean = value.trim().toLowerCase();
      if (clean.endsWith('s')) {
        return clean.slice(0, -1);
      }
      return clean;
    }

    getPlanCadence(plan, contextUpsellData = null) {
      if (!plan) {
        return { count: 0, unit: '' };
      }

      let count = this.parseIntervalCount(plan.interval_count);
      let unit = this.normalizeIntervalUnit(plan.interval_unit);

      if (!count || !unit) {
        const options = Array.isArray(plan.options) ? plan.options : [];
        for (const option of options) {
          if (option?.value) {
            const cadence = this.extractCadenceFromString(option.value);
            if (cadence.count && cadence.unit) {
              count = count || cadence.count;
              unit = unit || cadence.unit;
              break;
            }
          }
        }
      }

      if ((!count || !unit) && plan.name) {
        const cadenceFromName = this.extractCadenceFromString(plan.name);
        if (cadenceFromName.count && cadenceFromName.unit) {
          count = count || cadenceFromName.count;
          unit = unit || cadenceFromName.unit;
        }
      }

      if ((!count || !unit) && plan.description) {
        const cadenceFromDescription = this.extractCadenceFromString(plan.description);
        if (cadenceFromDescription.count && cadenceFromDescription.unit) {
          count = count || cadenceFromDescription.count;
          unit = unit || cadenceFromDescription.unit;
        }
      }

      if ((!count || !unit) && contextUpsellData) {
        const fallbackPlans = Array.isArray(contextUpsellData.selling_plans) ? contextUpsellData.selling_plans : [];
        if (fallbackPlans.length) {
          const fallbackCadence = this.getPlanCadence(fallbackPlans[0]);
          count = count || fallbackCadence.count;
          unit = unit || fallbackCadence.unit;
        }
      }

      return {
        count: count || 0,
        unit: unit || ''
      };
    }

    extractCadenceFromString(source) {
      if (!source || typeof source !== 'string') {
        return { count: 0, unit: '' };
      }

      const normalized = source.replace(/[-_]/g, ' ');
      const regex = /(\d+)\s*(day|days|week|weeks|month|months|year|years)/i;
      const match = normalized.match(regex);
      if (match) {
        return {
          count: this.parseIntervalCount(match[1]),
          unit: this.normalizeIntervalUnit(match[2])
        };
      }

      return { count: 0, unit: '' };
    }
  }

  window.BubsUpsellModalManager = UpsellModalManager;
})();

